/**
 * Date formatting helper utilities
 * Provides reusable date formatting functions for the application
 */

/**
 * Formats a date string to YYYYMMDD format
 * @param dateStr - Date string in various formats (YYYY-MM-DD, YYYYMMDD, or ISO format)
 * @returns Formatted date string in YYYYMMDD format, or empty string if invalid
 */
export function formatDateToYYYYMMDD(dateStr: string): string {
  if (!dateStr) return "";
  
  // If already in YYYYMMDD format (8 digits)
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  
  // If in yyyy-mm-dd format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr.replace(/-/g, "");
  }
  
  // Try parsing as Date object
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  }
  
  return "";
}

/**
 * Formats a date string from YYYYMMDD to YYYY-MM-DD
 * @param dateStr - Date string in YYYYMMDD format
 * @returns Formatted date string in YYYY-MM-DD format, or empty string if invalid
 */
export function formatDateFromYYYYMMDD(dateStr: string): string {
  if (!dateStr) return "";
  
  // If already in yyyy-mm-dd format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle yyyymmdd format (8 digits)
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }
  
  return "";
}

/**
 * Gets current date in YYYYMMDD format
 * @returns Current date string in YYYYMMDD format
 */
export function getCurrentDateYYYYMMDD(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

/**
 * Validates if a date string is in valid format
 * @param dateStr - Date string to validate
 * @returns True if valid date, false otherwise
 */
export function isValidDate(dateStr: string): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}
